import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { usePlan } from '@/context/PlanContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Clock, 
  LayoutDashboard, 
  Timer, 
  FileText, 
  Users, 
  Receipt, 
  BarChart3, 
  Settings, 
  CreditCard,
  LogOut,
  User,
  Menu,
  X,
  ChevronDown,
  Sun,
  Moon,
  FolderKanban,
  Calculator,
  Globe,
  HelpCircle,
  Lock
} from 'lucide-react';

export default function Layout(props) {
  const { user, logout, api, updateUser } = useAuth();
  const { theme, toggleTheme, isDark, setTheme } = useTheme();
  const { hasFeature } = usePlan();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get tour functions from props
  const { onStartTour } = props;

  // Handle dark mode toggle with plan check
  const handleThemeToggle = () => {
    if (!hasFeature('dark_mode')) {
      // If trying to enable dark mode without access, show upgrade message
      if (!isDark) {
        toast.error(i18n.language === 'en' 
          ? 'Dark mode is only available in Basic and Pro plans. Upgrade to unlock!'
          : 'Donkere modus is alleen beschikbaar in Basic en Pro. Upgrade om te ontgrendelen!');
        navigate('/abonnementen');
        return;
      }
    }
    toggleTheme();
  };

  // Force light mode for free users - use useEffect to avoid render loop
  useEffect(() => {
    if (!hasFeature('dark_mode') && isDark) {
      setTheme('light');
    }
  }, [hasFeature, isDark, setTheme]);

  function doLogout() {
    logout();
    navigate('/login');
  }

  function toggleMenu() {
    setMobileMenuOpen(!mobileMenuOpen);
  }

  function closeMenu() {
    setMobileMenuOpen(false);
  }

  async function changeLanguage(lang) {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    // Save to profile if logged in
    if (user && api) {
      try {
        await api.put('/auth/profile', { language: lang });
        updateUser({ language: lang });
      } catch (err) {
        console.error('Could not save language preference');
      }
    }
  }

  const p = location.pathname;
  const plan = user?.subscription_plan || 'free';
  const currentLang = i18n.language || 'nl';

  // Navigation link classes - adapt to light/dark mode
  function navLinkClass(path) {
    const isActive = p === path;
    if (isActive) {
      // Active state: cyan gradient background
      return 'flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-md shadow-cyan-500/20';
    }
    // Inactive: adapt to theme
    return 'flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-colors';
  }

  function getTitle() {
    if (p === '/dashboard') return t('nav.dashboard');
    if (p === '/timer') return t('nav.timer');
    if (p === '/registraties') return t('nav.registrations');
    if (p === '/klanten') return t('nav.clients');
    if (p === '/projecten') return t('nav.projects');
    if (p === '/facturen') return t('nav.invoices');
    if (p === '/creditnotas') return t('nav.creditNotes');
    if (p === '/rapporten') return t('nav.reports');
    if (p === '/km-calculator') return t('nav.kmCalculator');
    if (p === '/instellingen') return t('nav.settings');
    if (p === '/abonnementen') return t('nav.subscriptions');
    return t('nav.dashboard');
  }

  function getPlanClass() {
    if (plan === 'pro') return 'px-2 py-0.5 rounded-full text-xs text-white bg-gradient-to-r from-cyan-500 to-teal-500';
    if (plan === 'basic') return 'px-2 py-0.5 rounded-full text-xs text-white bg-blue-500';
    return 'px-2 py-0.5 rounded-full text-xs text-white bg-slate-500';
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Light mode: white bg, dark text | Dark mode: dark bg, light text */}
      <aside className="hidden lg:flex flex-col w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 fixed h-full z-50">
        <div className="p-6">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold font-heading text-slate-900 dark:text-white">Urenbuddy</span>
          </Link>
        </div>

        <nav className="flex-1 px-4 space-y-1">
          <Link to="/dashboard" data-testid="nav-dashboard" className={navLinkClass('/dashboard')}>
            <LayoutDashboard className="w-5 h-5" />
            <span className="font-medium">{t('nav.dashboard')}</span>
          </Link>
          <Link to="/timer" data-testid="nav-timer" className={navLinkClass('/timer')}>
            <Timer className="w-5 h-5" />
            <span className="font-medium">{t('nav.timer')}</span>
          </Link>
          <Link to="/registraties" data-testid="nav-registraties" className={navLinkClass('/registraties')}>
            <FileText className="w-5 h-5" />
            <span className="font-medium">{t('nav.registrations')}</span>
          </Link>
          <Link to="/klanten" data-testid="nav-klanten" className={navLinkClass('/klanten')}>
            <Users className="w-5 h-5" />
            <span className="font-medium">{t('nav.clients')}</span>
          </Link>
          <Link to="/projecten" data-testid="nav-projecten" className={navLinkClass('/projecten')}>
            <FolderKanban className="w-5 h-5" />
            <span className="font-medium">{t('nav.projects')}</span>
          </Link>
          <Link to="/facturen" data-testid="nav-facturen" className={navLinkClass('/facturen')}>
            <Receipt className="w-5 h-5" />
            <span className="font-medium">{t('nav.invoices')}</span>
          </Link>
          <Link to="/creditnotas" data-testid="nav-creditnotas" className={navLinkClass('/creditnotas')}>
            <FileText className="w-5 h-5 text-red-500" />
            <span className="font-medium">{t('nav.creditNotes')}</span>
          </Link>
          <Link to="/rapporten" data-testid="nav-rapporten" className={navLinkClass('/rapporten')}>
            <BarChart3 className="w-5 h-5" />
            <span className="font-medium">{t('nav.reports')}</span>
          </Link>
          <Link to="/km-calculator" data-testid="nav-km-calculator" className={navLinkClass('/km-calculator')}>
            <Calculator className="w-5 h-5" />
            <span className="font-medium">{t('nav.kmCalculator')}</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <div className="mb-2">
            <Link to="/abonnementen" data-testid="nav-abonnementen" className={navLinkClass('/abonnementen')}>
              <CreditCard className="w-5 h-5" />
              <span className="font-medium">{t('nav.subscriptions')}</span>
            </Link>
          </div>
          <Link to="/instellingen" data-testid="nav-instellingen" className={navLinkClass('/instellingen')}>
            <Settings className="w-5 h-5" />
            <span className="font-medium">{t('nav.settings')}</span>
          </Link>
        </div>
      </aside>

      {/* Mobile header - adapts to theme */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold font-heading text-slate-900 dark:text-white">Urenbuddy</span>
          </Link>
          <button onClick={toggleMenu} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-700 dark:text-slate-300" data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu - adapts to theme */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white dark:bg-slate-900 pt-16">
          <nav className="p-4 space-y-1">
            <Link to="/dashboard" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <LayoutDashboard className="w-5 h-5" /><span>Dashboard</span>
            </Link>
            <Link to="/timer" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <Timer className="w-5 h-5" /><span>Timer</span>
            </Link>
            <Link to="/registraties" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <FileText className="w-5 h-5" /><span>Registraties</span>
            </Link>
            <Link to="/klanten" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <Users className="w-5 h-5" /><span>Klanten</span>
            </Link>
            <Link to="/projecten" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <FolderKanban className="w-5 h-5" /><span>Projecten</span>
            </Link>
            <Link to="/facturen" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <Receipt className="w-5 h-5" /><span>Facturen</span>
            </Link>
            <Link to="/creditnotas" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <FileText className="w-5 h-5 text-red-500" /><span>Creditnota's</span>
            </Link>
            <Link to="/rapporten" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <BarChart3 className="w-5 h-5" /><span>Rapporten</span>
            </Link>
            <Link to="/km-calculator" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
              <Calculator className="w-5 h-5" /><span>KM Calculator</span>
            </Link>
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
              <Link to="/abonnementen" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
                <CreditCard className="w-5 h-5" /><span>Abonnementen</span>
              </Link>
              <Link to="/instellingen" onClick={closeMenu} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
                <Settings className="w-5 h-5" /><span>Instellingen</span>
              </Link>
              <button onClick={doLogout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 w-full">
                <LogOut className="w-5 h-5" /><span>Uitloggen</span>
              </button>
              
              {/* Mobile Dark Mode Toggle */}
              <button 
                onClick={handleThemeToggle}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 w-full"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                <span>{isDark ? 'Lichte modus' : 'Donkere modus'}</span>
                {!hasFeature('dark_mode') && !isDark && <Lock className="w-4 h-4 text-amber-500 ml-auto" />}
              </button>
              
              {/* Mobile Help/Tour Button */}
              <button 
                onClick={() => { closeMenu(); onStartTour && onStartTour(); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 w-full"
              >
                <HelpCircle className="w-5 h-5" />
                <span>{t('onboarding.restartTour')}</span>
              </button>
            </div>
          </nav>
        </div>
      )}

      <main className="flex-1 lg:ml-64">
        {/* Desktop header - adapts to theme */}
        <header className="hidden lg:block sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
          <div className="px-8 py-4 flex items-center justify-between">
            <h1 className="text-xl font-semibold font-heading text-slate-900 dark:text-white">{getTitle()}</h1>
            
            <div className="flex items-center gap-4">
              {/* Help/Tour Button */}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onStartTour}
                className="rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid="help-tour-btn"
                title={t('onboarding.restartTour')}
              >
                <HelpCircle className="w-5 h-5" />
              </Button>

              {/* Dark Mode Toggle */}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleThemeToggle}
                className="rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 relative"
                data-testid="theme-toggle"
                title={isDark ? 'Lichte modus' : 'Donkere modus'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                {!hasFeature('dark_mode') && !isDark && (
                  <Lock className="w-3 h-3 absolute -top-1 -right-1 text-amber-500" />
                )}
              </Button>

              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="language-switcher">
                    <Globe className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => changeLanguage('nl')} className={currentLang === 'nl' ? 'bg-slate-100 dark:bg-slate-800' : ''}>
                    <span className="mr-2">🇳🇱</span> Nederlands
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage('en')} className={currentLang === 'en' ? 'bg-slate-100 dark:bg-slate-800' : ''}>
                    <span className="mr-2">🇬🇧</span> English
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{user?.bedrijfsnaam || user?.email}</p>
                    <span className={getPlanClass()}>{plan.charAt(0).toUpperCase() + plan.slice(1)}</span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/instellingen" className="flex items-center gap-2">
                    <Settings className="w-4 h-4" /><span>{t('nav.settings')}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/abonnementen" className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /><span>{t('nav.subscriptions')}</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /><span>{t('auth.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </header>
        <div className="p-4 lg:p-8 pt-20 lg:pt-8">{props.children}</div>
      </main>
    </div>
  );
}
